import React from 'react';
import { CalendarClock, IndianRupee, Gift } from 'lucide-react';

/**
 * Free Demo or Subscription — for AGENTS ONLY.
 *
 * Admin, Recovery Department and Branch Manager are free and never see this.
 *
 * Nothing here blocks anyone. When an end date passes, the administrator sees
 * "Expired" on the user list and decides what to do. The app does not cut an
 * agent off in the middle of a collection on its own, because the
 * specification never said it should and the cost of guessing wrong is a
 * field agent locked out in front of a customer.
 */

export interface AgentPlan {
  plan: '' | 'demo' | 'subscription';
  demoStartDate: string;
  demoEndDate: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  subscriptionAmount: string;
  paymentDate: string;
  paymentMode: string;
  paymentReference: string;
}

export const emptyAgentPlan: AgentPlan = {
  plan: '',
  demoStartDate: '',
  demoEndDate: '',
  subscriptionStartDate: '',
  subscriptionEndDate: '',
  subscriptionAmount: '',
  paymentDate: '',
  paymentMode: '',
  paymentReference: '',
};

/** Today, and today + n days, as yyyy-mm-dd for a date input. */
export function isoDay(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

interface Props {
  value: AgentPlan;
  onChange: (next: AgentPlan) => void;
}

export const AgentPlanFields: React.FC<Props> = ({ value, onChange }) => {
  const set = (patch: Partial<AgentPlan>) => onChange({ ...value, ...patch });

  const fieldCls =
    'w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none';

  const pick = (plan: 'demo' | 'subscription') => {
    if (plan === 'demo') {
      set({
        plan: 'demo',
        // Sensible starting dates so nobody has to type both. A 15-day demo
        // is a default, not a rule -- either date can be changed.
        demoStartDate: value.demoStartDate || isoDay(0),
        demoEndDate: value.demoEndDate || isoDay(15),
      });
    } else {
      set({
        plan: 'subscription',
        subscriptionStartDate: value.subscriptionStartDate || isoDay(0),
        subscriptionEndDate: value.subscriptionEndDate || isoDay(365),
        paymentDate: value.paymentDate || isoDay(0),
      });
    }
  };

  const card = (active: boolean) =>
    `flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border font-bold cursor-pointer transition text-left ${
      active
        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
        : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'
    }`;

  return (
    <div className="p-3 bg-emerald-50/60 border border-emerald-100 rounded-xl space-y-3 text-xs">
      <div className="flex items-center gap-2 text-emerald-900 font-bold">
        <CalendarClock className="w-4 h-4" />
        <span>Agent plan *</span>
      </div>
      <p className="text-[11px] text-slate-600">
        Only agents are subscription-controlled. Admin, Recovery Department and
        Branch Manager are free. An expired date is flagged to you — it does not
        lock the agent out.
      </p>

      <div className="flex gap-2">
        <button type="button" onClick={() => pick('demo')} className={card(value.plan === 'demo')}>
          <Gift className="w-4 h-4 shrink-0" />
          <span>Free Demo</span>
        </button>
        <button
          type="button"
          onClick={() => pick('subscription')}
          className={card(value.plan === 'subscription')}
        >
          <IndianRupee className="w-4 h-4 shrink-0" />
          <span>Subscription</span>
        </button>
      </div>

      {value.plan === 'demo' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-slate-700 font-bold mb-1">Demo start *</label>
            <input
              type="date"
              value={value.demoStartDate}
              onChange={(e) => set({ demoStartDate: e.target.value })}
              className={fieldCls}
            />
          </div>
          <div>
            <label className="block text-slate-700 font-bold mb-1">Demo end *</label>
            <input
              type="date"
              value={value.demoEndDate}
              onChange={(e) => set({ demoEndDate: e.target.value })}
              className={fieldCls}
            />
          </div>
        </div>
      )}

      {value.plan === 'subscription' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-slate-700 font-bold mb-1">Subscription start *</label>
            <input
              type="date"
              value={value.subscriptionStartDate}
              onChange={(e) => set({ subscriptionStartDate: e.target.value })}
              className={fieldCls}
            />
          </div>
          <div>
            <label className="block text-slate-700 font-bold mb-1">Subscription end *</label>
            <input
              type="date"
              value={value.subscriptionEndDate}
              onChange={(e) => set({ subscriptionEndDate: e.target.value })}
              className={fieldCls}
            />
          </div>
          <div>
            <label className="block text-slate-700 font-bold mb-1">Amount (₹) *</label>
            <input
              type="number"
              min="0"
              step="1"
              value={value.subscriptionAmount}
              onChange={(e) => set({ subscriptionAmount: e.target.value })}
              placeholder="e.g. 12000"
              className={`${fieldCls} font-mono font-bold`}
            />
          </div>
          <div>
            <label className="block text-slate-700 font-bold mb-1">Payment date *</label>
            <input
              type="date"
              value={value.paymentDate}
              onChange={(e) => set({ paymentDate: e.target.value })}
              className={fieldCls}
            />
          </div>
          <div>
            <label className="block text-slate-700 font-bold mb-1">Payment mode *</label>
            <select
              value={value.paymentMode}
              onChange={(e) => set({ paymentMode: e.target.value })}
              className={fieldCls}
            >
              <option value="">Select…</option>
              <option value="UPI">UPI</option>
              <option value="NEFT/RTGS">NEFT / RTGS</option>
              <option value="IMPS">IMPS</option>
              <option value="Cheque">Cheque</option>
              <option value="Cash">Cash</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-slate-700 font-bold mb-1">
              UTR / Reference{' '}
              <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              value={value.paymentReference}
              onChange={(e) => set({ paymentReference: e.target.value })}
              placeholder="Optional"
              className={`${fieldCls} font-mono`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentPlanFields;
